import { useCallback } from 'react';
import useV2Mutation from 'utils/useV2Mutation';
import { toastSuccess, toastError } from 'bench-internal-theme';
import { QueryResult } from '@apollo/client';
import { logError } from '../../../../utils/errorHandling';
import {
  CLASSIFY_PB_TRANSACTIONS,
  CONFIRM_PERSONAL_BUSINESS_CLASSIFICATIONS,
} from '../../../mutation';
import { GET_PERSONAL_BUSINESS_UNCLASSIFIED_DETAILS } from '../../../queries';
import {
  DataEntryRowDataType,
  JournalEntryClassificationValue,
  PersonalBusinessDetails,
  ClassifyPersonalBusinessDataEntriesResult,
  ClassifyPersonalBusinessDataEntriesVariables,
  ConfirmPersonalBusinessClassificationsResult,
  ConfirmPersonalBusinessClassificationsVariables,
} from '../types';

interface UsePersonalBusinessClassificationProps {
  clientId: string;
  searchMarker?: string;
  updateQuery: QueryResult<PersonalBusinessDetails>['updateQuery'];
}

interface UsePersonalBusinessClassificationReturn {
  classifyTransactions: (
    dataEntryIds: string[],
    classificationValue: JournalEntryClassificationValue,
  ) => Promise<void>;
  confirmBusinessClassifications: (dataEntryIds: string[], count: number) => Promise<void>;
  confirmPersonalClassifications: (dataEntryIds: string[], count: number) => Promise<void>;
  classifyLoading: boolean;
  confirmLoading: boolean;
  handleOptimisticUpdate: (
    row: DataEntryRowDataType,
    classificationValue?: JournalEntryClassificationValue,
  ) => void;
}

export const usePersonalBusinessClassification = ({
  clientId,
  searchMarker,
  updateQuery,
}: UsePersonalBusinessClassificationProps): UsePersonalBusinessClassificationReturn => {
  const [classifyPersonalBusinessTransactions, { loading: classifyLoading }] = useV2Mutation<
    ClassifyPersonalBusinessDataEntriesResult,
    ClassifyPersonalBusinessDataEntriesVariables
  >(CLASSIFY_PB_TRANSACTIONS);

  const [confirmPersonalBusinessClassifications, { loading: confirmLoading }] = useV2Mutation<
    ConfirmPersonalBusinessClassificationsResult,
    ConfirmPersonalBusinessClassificationsVariables
  >(CONFIRM_PERSONAL_BUSINESS_CLASSIFICATIONS, {
    refetchQueries: [GET_PERSONAL_BUSINESS_UNCLASSIFIED_DETAILS],
    awaitRefetchQueries: true,
  });

  // Handle optimistic updates for immediate UI feedback
  const handleOptimisticUpdate = useCallback(
    (row: DataEntryRowDataType, classificationValue?: JournalEntryClassificationValue) => {
      updateQuery(prev => ({
        personalBusinessClassificationDetailsByClient: {
          ...prev.personalBusinessClassificationDetailsByClient,
          dataEntries: {
            ...prev.personalBusinessClassificationDetailsByClient.dataEntries,
            dataEntries:
              prev.personalBusinessClassificationDetailsByClient.dataEntries.dataEntries.map(
                dataEntry => ({
                  ...dataEntry,
                  journalEntry: {
                    ...dataEntry.journalEntry,
                    classification: {
                      ...dataEntry.journalEntry.classification,
                      value:
                        dataEntry.id === row.id
                          ? classificationValue
                            ? classificationValue
                            : dataEntry.journalEntry.classification.value === 'BUSINESS'
                            ? 'PERSONAL'
                            : 'BUSINESS'
                          : dataEntry.journalEntry.classification.value,
                    },
                  },
                }),
              ),
          },
        },
      }));
    },
    [updateQuery],
  );

  const classifyTransactions = useCallback(
    async (dataEntryIds: string[], classificationValue: JournalEntryClassificationValue) => {
      if (dataEntryIds.length === 0) {
        return;
      }

      try {
        await classifyPersonalBusinessTransactions({
          variables: {
            input: {
              searchMarker,
              clientId,
              dataEntryIds,
              classificationValue,
            },
            skipDataEntries: false,
          },
        });
      } catch (error) {
        logError(error as Error, undefined, 'classifyPersonalBusinessTransactions');
        toastError('Failed to classify transactions');
        throw error;
      }
    },
    [classifyPersonalBusinessTransactions, clientId, searchMarker],
  );

  const confirmBusinessClassifications = useCallback(
    async (dataEntryIds: string[], count: number) => {
      if (dataEntryIds.length === 0) {
        return;
      }

      try {
        await confirmPersonalBusinessClassifications({
          variables: {
            input: {
              searchMarker: searchMarker || '',
              clientId,
              dataEntryIds,
              classificationValueToConfirm: 'BUSINESS',
            },
          },
        });
        toastSuccess(`Successfully submitted ${count} business transactions`);
      } catch (error: unknown) {
        logError(error as Error, undefined, 'confirmPersonalBusinessClassifications');
        toastError('Failed to submit transactions');
        throw error;
      }
    },
    [confirmPersonalBusinessClassifications, clientId, searchMarker],
  );

  const confirmPersonalClassifications = useCallback(
    async (dataEntryIds: string[], count: number) => {
      if (dataEntryIds.length === 0) {
        return;
      }

      try {
        await confirmPersonalBusinessClassifications({
          variables: {
            input: {
              searchMarker: searchMarker || '',
              clientId,
              dataEntryIds,
              classificationValueToConfirm: 'PERSONAL',
            },
          },
        });
        toastSuccess(`Successfully submitted ${count} personal transactions`);
      } catch (error: unknown) {
        logError(error as Error, undefined, 'confirmPersonalBusinessClassifications');
        toastError('Failed to submit transactions');
        throw error;
      }
    },
    [confirmPersonalBusinessClassifications, clientId, searchMarker],
  );

  return {
    classifyTransactions,
    confirmBusinessClassifications,
    confirmPersonalClassifications,
    classifyLoading,
    confirmLoading,
    handleOptimisticUpdate,
  };
};
